import React, { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

type MathToken = {
  value: string;
  math: boolean;
  display: boolean;
};

const MATH_PATTERN = /(\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)|\$\$[\s\S]*?\$\$|\$(?:\\.|[^$])*?\$)/g;

function tokenizeMath(content: string): MathToken[] {
  const tokens: MathToken[] = [];
  let lastIndex = 0;
  for (const match of content.matchAll(MATH_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) tokens.push({ value: content.slice(lastIndex, index), math: false, display: false });
    const raw = match[0];
    const display = raw.startsWith('$$') || raw.startsWith('\\[');
    const delimiterLength = raw.startsWith('$$') || raw.startsWith('\\(') || raw.startsWith('\\[') ? 2 : 1;
    tokens.push({ value: raw.slice(delimiterLength, -delimiterLength), math: true, display });
    lastIndex = index + raw.length;
  }
  if (lastIndex < content.length) tokens.push({ value: content.slice(lastIndex), math: false, display: false });
  return tokens.length > 0 ? tokens : [{ value: content, math: false, display: false }];
}

const MathText: React.FC<{ content?: string | null; className?: string }> = ({ content, className = '' }) => {
  const text = String(content ?? '');
  const tokens = useMemo(() => tokenizeMath(text), [text]);

  return (
    <span className={`math-text whitespace-pre-wrap ${className}`.trim()}>
      {tokens.map((token, index) => {
        if (!token.math) return <React.Fragment key={index}>{token.value}</React.Fragment>;
        let html = '';
        try {
          html = katex.renderToString(token.value, {
            displayMode: token.display,
            throwOnError: false,
            strict: 'warn',
            trust: false,
            output: 'htmlAndMathml',
          });
        } catch {
          return <code key={index} className="rounded bg-amber-50 px-1 text-amber-800">{token.value}</code>;
        }
        return (
          <span
            key={index}
            className={token.display ? 'my-2 block overflow-x-auto py-1' : 'inline-block max-w-full align-middle'}
            // Only KaTeX-generated, trust-disabled output is inserted. Database text never enters HTML directly.
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      })}
    </span>
  );
};

export default React.memo(MathText);
